/**
 * Unit conversion for what the web SHOWS: a mirror of the server's units.ConvertBetween (platform-core/internal/units
 * display.go) over the same table, held to the same file of vectors (platform-core/internal/units/testdata/
 * display_vectors.json) by both suites, so a stored value reads the same on the screen as the server converts it.
 *
 * Nothing stored is rewritten: a value is converted for the screen only. Every conversion is AFFINE (scale and
 * offset): 1832 °F is 1000 °C, and a scale alone gives 1017.8. It goes through the canonical unit of the dimension
 * (°C, Pa, kg, mm, ml, L/min, s), the way the server's ingest does.
 *
 * The table is the server's, with one difference in kind: the server's also says which units an import rewrites; that
 * is no business of a display, so it is left out. Add a unit to the server's table, add it here, and the vectors fail
 * until the two agree.
 */

/** The canonical unit of each dimension: the keys of a choice's overrides. */
export type DimensionKey = "°C" | "Δ°C" | "Pa" | "kg" | "mm" | "ml" | "L/min" | "s";

interface Conversion {
  canonical: DimensionKey;
  scale: number;
  offset: number;
}

const conv = (canonical: DimensionKey, scale: number, offset = 0): Conversion => ({ canonical, scale, offset });

// canonical = value * scale + offset. Keys are the normalised spelling (lowercase).
const TABLE: Record<string, Conversion> = {
  // mass
  kg: conv("kg", 1),
  g: conv("kg", 0.001),
  mg: conv("kg", 1e-6),
  t: conv("kg", 1000),
  lb: conv("kg", 0.45359237),
  oz: conv("kg", 0.028349523125),
  // temperature, affine: °C is canonical
  "°c": conv("°C", 1),
  "°f": conv("°C", 5 / 9, (-32 * 5) / 9),
  k: conv("°C", 1, -273.15),
  // temperature difference (a superheat, a drop): a scale only, never shifted; Δ lower-cases to δ
  "δ°c": conv("Δ°C", 1),
  "δ°f": conv("Δ°C", 5 / 9),
  δk: conv("Δ°C", 1),
  // pressure
  psi: conv("Pa", 6894.757293168),
  pa: conv("Pa", 1),
  kpa: conv("Pa", 1000),
  mpa: conv("Pa", 1e6),
  bar: conv("Pa", 100000),
  mbar: conv("Pa", 100),
  // a green strength in grams-force per square centimetre
  "g/cm²": conv("Pa", 98.0665),
  // length
  in: conv("mm", 25.4),
  ft: conv("mm", 304.8),
  mm: conv("mm", 1),
  cm: conv("mm", 10),
  m: conv("mm", 1000),
  // volume
  m3: conv("ml", 1e6),
  l: conv("ml", 1000),
  ml: conv("ml", 1),
  gal: conv("ml", 3785.411784),
  // flow
  "l/min": conv("L/min", 1),
  "gal/min": conv("L/min", 3.785411784),
  // time
  s: conv("s", 1),
  sec: conv("s", 1),
  min: conv("s", 60),
  h: conv("s", 3600),
  hr: conv("s", 3600),
};

// The spellings real spreadsheets contain, folded onto the table's keys.
const ALIASES: Record<string, string> = {
  kgs: "kg", kilogram: "kg", kilograms: "kg",
  gram: "g", grams: "g",
  tonne: "t", tonnes: "t", mt: "t", ton: "t",
  lbs: "lb", pound: "lb", pounds: "lb",
  c: "°c", degc: "°c", "deg c": "°c", celsius: "°c", centigrade: "°c",
  f: "°f", degf: "°f", "deg f": "°f", fahrenheit: "°f",
  kelvin: "k",
  delta_degc: "δ°c", "delta degc": "δ°c", delta_c: "δ°c", δc: "δ°c", "δ c": "δ°c",
  delta_degf: "δ°f", "delta degf": "δ°f", delta_f: "δ°f", δf: "δ°f", "δ f": "δ°f",
  delta_k: "δk", "δ k": "δk",
  bars: "bar",
  litre: "l", litres: "l", liter: "l", liters: "l",
  gallon: "gal", gallons: "gal", gals: "gal",
  lpm: "l/min", "litres/min": "l/min", "liters/min": "l/min", "l/m": "l/min",
  gpm: "gal/min", "gallons/min": "gal/min",
  "g/cm2": "g/cm²", "gf/cm²": "g/cm²", "gf/cm2": "g/cm²",
  inch: "in", inches: "in", '"': "in",
  minute: "min", minutes: "min", mins: "min",
  hour: "h", hours: "h", hrs: "h",
  second: "s", seconds: "s", secs: "s",
};

// The system each unit belongs to, by its key: the server's unitSystem. Time's units belong to neither.
const SYSTEM_OF: Record<string, "si" | "us"> = {
  "°f": "us", psi: "us", lb: "us", oz: "us", in: "us", ft: "us", "δ°f": "us",
  "°c": "si", k: "si", "δ°c": "si", δk: "si",
  pa: "si", kpa: "si", mpa: "si", bar: "si", mbar: "si",
  kg: "si", g: "si", mg: "si", t: "si",
  mm: "si", cm: "si", m: "si",
  ml: "si", l: "si", m3: "si", gal: "us",
  "l/min": "si", "gal/min": "us",
  "g/cm²": "si",
};

// The units whose table key is not how a person writes them.
const LABELS: Record<string, string> = {
  "°c": "°C", "°f": "°F", k: "K",
  "δ°c": "Δ°C", "δ°f": "Δ°F", δk: "ΔK",
  pa: "Pa", kpa: "kPa", mpa: "MPa",
  sec: "s", hr: "h",
  l: "L", "l/min": "L/min",
};

const DIMENSION_WORDS: Record<DimensionKey, string> = {
  "°C": "temperature",
  "Δ°C": "temperature difference",
  Pa: "pressure",
  kg: "mass",
  mm: "length",
  ml: "volume",
  "L/min": "flow",
  s: "time",
};

const has = (table: object, key: string) => Object.prototype.hasOwnProperty.call(table, key);

/** A conversion the table cannot make: an unknown unit, or two units of different dimensions. */
export class UnitConversionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnitConversionError";
  }
}

/**
 * Millitesla, a magnetic separator's field strength, is written "mT". Lower-cased it would read "mt", a metric tonne,
 * and a 350 mT field was shown in pounds (the 2026-10-24 page sweep, H8). No unit the table converts: kept as written
 * and shown as recorded, as the server's Normalise does.
 */
export const MILLITESLA = "mT";

/**
 * Folds a unit to the table's key form: trimmed, lowercased, alias-resolved, with the masculine ordinal a paste
 * leaves for the degree sign read as one. The one unit whose case carries its meaning, millitesla, is kept as written.
 * As the server's Normalise does.
 */
export function normalise(unit: string): string {
  const t = unit.trim();
  if (t === MILLITESLA) return MILLITESLA;
  const u = t.toLowerCase().replaceAll("º", "°");
  return has(ALIASES, u) ? ALIASES[u] : u;
}

function lookup(unit: string): Conversion | undefined {
  const key = normalise(unit);
  return has(TABLE, key) ? TABLE[key] : undefined;
}

/** The canonical unit of the dimension a unit belongs to (°C for °F, Pa for psi), or "" when the unit is unknown. */
export function dimensionOf(unit: string): DimensionKey | "" {
  return lookup(unit)?.canonical ?? "";
}

/** The system a unit belongs to ("si" or "us"), whatever its spelling; "" for time's units and an unknown unit. */
export function systemOfUnit(unit: string): "si" | "us" | "" {
  const key = normalise(unit);
  return has(SYSTEM_OF, key) ? SYSTEM_OF[key] : "";
}

/** A known unit as it is written on screen (°F, kPa, psi), whatever its spelling; an unknown unit as given, trimmed. */
export function labelOf(unit: string): string {
  const key = normalise(unit);
  if (!has(TABLE, key)) return unit.trim();
  return has(LABELS, key) ? LABELS[key] : key;
}

/**
 * Converts a value from one unit to another through the canonical unit, affine both ways. The same unit after
 * normalising is the identity, known or not (a value shown as stored needs no table). Otherwise an unknown unit, or a
 * pair of different dimensions (°C to kg), throws a UnitConversionError that names the units.
 */
export function convertBetween(value: number, from: string, to: string): number {
  if (normalise(from) === normalise(to)) return value;
  const cf = lookup(from);
  if (!cf) throw new UnitConversionError(`"${from.trim()}" is not a unit this platform knows`);
  const ct = lookup(to);
  if (!ct) throw new UnitConversionError(`"${to.trim()}" is not a unit this platform knows`);
  if (cf.canonical !== ct.canonical) {
    throw new UnitConversionError(
      `${from.trim()} (${DIMENSION_WORDS[cf.canonical]}) cannot be converted to ${to.trim()} (${DIMENSION_WORDS[ct.canonical]})`,
    );
  }
  const canonical = value * cf.scale + cf.offset;
  return (canonical - ct.offset) / ct.scale;
}
